import {
  Paper,
  Stack,
  Text,
  Group,
  Tooltip,
  ActionIcon,
  Alert,
} from "@mantine/core";
import { IconQuestionMark, IconInfoCircle } from "@tabler/icons-react";
import { caseTypeLabel, type CaseType } from "../../utils/deriveCaseType";

const WORKFLOW_DESCRIPTIONS: Record<CaseType, string> = {
  global:
    "GCP performs both technical component (lab work) and professional component (pathologist reads and signs the report).",
  pass_through:
    "Blocks and/or slides are received already processed from an external source — no grossing or embedding is done. GCP cuts any additional slides needed and the pathologist reads and signs the report (billed as Global).",
  consultation:
    "GCP pathologist reviews slides sent from another facility. No technical component is performed by GCP.",
  tc_only:
    "GCP performs technical component work only. Slides ship back to the practice when done. GCP pathologist does not read these cases.",
  grossing_only:
    "GCP records the specimen gross description, then sends the case directly to pathologist review without creating blocks, slides, or stains.",
};

interface DerivedWorkflowCardProps {
  caseType: CaseType;
}

/**
 * Left-sidebar card showing the workflow (case type) derived from specimen materials + signals.
 * Presented in the same uppercase-label / value style as the facility summary, with a help
 * tooltip and an info card describing what the workflow means. Workflow is never chosen manually.
 */
export default function DerivedWorkflowCard({
  caseType,
}: DerivedWorkflowCardProps) {
  return (
    <Paper p="sm" withBorder>
      <Stack gap="xs">
        <div>
          <Group gap={4} mb={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
              Workflow
            </Text>
            <Tooltip
              label="Derived automatically from the materials received and case signals — it determines the case's workflow. It is not chosen manually."
              position="top"
              withinPortal
            >
              <ActionIcon
                size="xs"
                variant="subtle"
                color="gray"
                aria-label="Workflow help"
              >
                <IconQuestionMark size={12} />
              </ActionIcon>
            </Tooltip>
          </Group>

          <Text size="sm" fw={500}>
            {caseTypeLabel(caseType)}
          </Text>
        </div>

        <Alert
          icon={<IconInfoCircle size={13} />}
          color="blue"
          variant="light"
          role="alert"
          py={6}
          px={8}
          styles={{ message: { fontSize: "11px", lineHeight: 1.35 } }}
        >
          {WORKFLOW_DESCRIPTIONS[caseType]}
        </Alert>
      </Stack>
    </Paper>
  );
}
